import { setAccessToken } from '@/utils/accessToken'
import { saveLoginUserProfile, saveTokenExpireMeta, setLoggedInFlag, saveRememberMePreference } from '@/utils/userSession'

/**
 * 后端统一包：{ code, msg, data }
 * 兼容 code 为数字 200、字符串 "200"
 */
function unwrapBusinessData(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw
  }
  if (!Object.prototype.hasOwnProperty.call(raw, 'code')) {
    return raw
  }
  const n = typeof raw.code === 'string' ? Number(String(raw.code).trim()) : Number(raw.code)
  if (Number.isNaN(n) || n !== 200) {
    const msg =
      raw.msg != null && String(raw.msg).trim() !== '' ? String(raw.msg) : '刷新会话失败'
    const err = new Error(msg)
    err.isAuthPayloadError = true
    throw err
  }
  return raw.data != null ? raw.data : {}
}

/**
 * 从登录或 refresh 响应中解析 accessToken 与用户信息并落库（内存 Token + localStorage 用户资料）。
 * 兼容多种字段命名，便于与后端联调。
 *
 * @param {*} raw axios response.data（可能是已解包的一层对象）
 * @param {{ fallbackUsername?: string }} [options] 登录接口未返回 userId 时可用账号兜底
 */
export function applyLoginOrRefreshPayload(raw, options = {}) {
  const payload = unwrapBusinessData(raw)
  const root = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}

  const token =
    root.accessToken ||
    root.access_token ||
    root.token ||
    (root.auth && root.auth.accessToken) ||
    ''

  if (!token || String(token).trim() === '') {
    const err = new Error('响应中未包含 accessToken')
    err.isAuthPayloadError = true
    throw err
  }

  setAccessToken(String(token).trim())

  const accessExp = root.accessTokenExpireTime ?? root.access_token_expire_time
  const refreshExp = root.refreshTokenExpireTime ?? root.refresh_token_expire_time
  const expireMeta = {}
  if (accessExp != null && String(accessExp).trim() !== '') {
    expireMeta.accessTokenExpireTime = accessExp
  }
  if (refreshExp != null && String(refreshExp).trim() !== '') {
    expireMeta.refreshTokenExpireTime = refreshExp
  }
  if (Object.keys(expireMeta).length) {
    saveTokenExpireMeta(expireMeta)
  }

  const userObj =
    (root.user && typeof root.user === 'object' ? root.user : null) ||
    (root.profile && typeof root.profile === 'object' ? root.profile : null)

  const pickName = (o) => {
    if (!o || typeof o !== 'object') return ''
    const keys = ['userName', 'username', 'name', 'nickName', 'realName']
    for (const k of keys) {
      const v = o[k]
      if (v != null && String(v).trim() !== '') return String(v).trim()
    }
    return ''
  }

  const base = userObj || root
  let userId =
    (base.userId != null && String(base.userId).trim() !== '' && String(base.userId).trim()) ||
    (base.id != null && String(base.id).trim() !== '' && String(base.id).trim()) ||
    ''

  if (!userId && options.fallbackUsername != null && String(options.fallbackUsername).trim() !== '') {
    userId = String(options.fallbackUsername).trim()
  }

  const userName = pickName(base) || userId
  const gender =
    base.gender != null && String(base.gender).trim() !== ''
      ? base.gender
      : base.sex != null && String(base.sex).trim() !== ''
        ? base.sex
        : ''
  const avatar =
    base.avatar != null && String(base.avatar).trim() !== ''
      ? String(base.avatar).trim()
      : base.avatarUrl != null && String(base.avatarUrl).trim() !== ''
        ? String(base.avatarUrl).trim()
        : base.avatarPath != null && String(base.avatarPath).trim() !== ''
          ? String(base.avatarPath).trim()
          : ''

  if (userId) {
    saveLoginUserProfile({ userId, userName: userName || userId, gender, avatar })
  }
  setLoggedInFlag()
  if (options.rememberMe === true) {
    saveRememberMePreference(true)
  } else if (options.rememberMe === false) {
    saveRememberMePreference(false)
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:login'))
  }
}
